import {
  FilterSectionData,
  FilterSectionDisplayData,
} from "@shared/features/filters/types";
import { useDispatch, useSelector } from "react-redux";
import { toggleArrayItem } from "@shared/utils/arrays";
import {
  ICON_TYPES,
  MY_TV_SIGNALS_BOT_FILTERS_SLICE_NAME,
} from "@shared/common/constants";
import {
  BACKTESTING_STATUS_TO_DISPLAY,
  DELETED_ITEM_REGEX,
  FILTER_TYPES_DATA,
  FOLLOW_STATUS_TO_DISPLAY,
  SIGNAL_STATUS_TO_DISPLAY,
  STATUS_TO_DISPLAY,
} from "@shared/features/filters/constants";
import {
  BacktestingFilterStatus,
  CHECKBOX_FILTER_TYPES,
  CheckboxFilterType,
  EntryZoneCategory,
  FiltersActions,
  FiltersData,
  FilterSectionType,
  FiltersSelector,
  FilterType,
  LanguageNameSpaces,
  RANGE_FILTER_TYPES,
  Status,
  WorkspaceType,
} from "@shared/types";
import {
  getChipPreviewText,
  getIsAllSelected,
  isNumberArray,
} from "@shared/features/filters/utils";
import { max, min } from "lodash";
import { useMemo } from "react";
import { sortByStringProperty } from "@shared/utils/general";
import { isStringsArray } from "@shared/types/utils";
import { useClientsData } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { FollowSignalStatus } from "@shared/features/signals/common/types";
import { selectWorkspace } from "@shared/slices/common.slice";
import { TranslationKeys } from "@shared/common/i18n/types";

export const useFilter = <T extends FilterType>({
  filterType,
  filtersActions,
  filtersSelectors,
  isMobile = false,
}: {
  filterType: T;
  filtersActions: FiltersActions;
  filtersSelectors: FiltersSelector;
  isMobile?: boolean;
}): FilterSectionData<T> => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const dispatch = useDispatch();
  const workspace = useSelector(selectWorkspace);
  const filtersSliceData = useSelector(filtersSelectors.selectFilters);
  const { filters, config, mappings } = filtersSliceData;
  const { getAccountByName } = useClientsData();
  const hideDeletedSelector = useMemo(
    () => filtersSelectors.selectHideDeletedOptions(filterType),
    [filterType, filtersSelectors]
  );
  const showHideDeletedToggle = useSelector(hideDeletedSelector);
  const hideDeletedToggleActive = useSelector(
    filtersSelectors.selectHideDeletedToggleActive
  );
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const isTvToSignalsGroupsFilter =
    filterType === FilterType.MULTI_GROUPS &&
    config.baseSliceName === MY_TV_SIGNALS_BOT_FILTERS_SLICE_NAME;

  const filterData = filters[filterType];
  const filterSectionType = FILTER_TYPES_DATA[filterType].filterSectionType;
  const setFunctionName =
    FILTER_TYPES_DATA[filterType as FilterType].setActionName;
  const setFilterValue = setFunctionName && filtersActions[setFunctionName];

  let clientToExchangeMapping: Record<string, string> = {};
  Object.entries(mappings.exchangeToClientsMapping || {}).forEach(
    ([exchangeName, clients]) => {
      clients?.forEach((clientName) => {
        clientToExchangeMapping[clientName] = exchangeName;
      });
    }
  );

  const toggleItem: FilterSectionData<T>["toggleItem"] = (value) => {
    if (setFilterValue) {
      dispatch(
        setFilterValue(toggleArrayItem((filterData?.value || []) as any, value))
      );
    }
  };

  const onChange: FilterSectionData<T>["onChange"] = (value) => {
    if (setFilterValue) {
      dispatch(setFilterValue(value as any));
    }
  };

  const getGroupData = (subGroupId: number) => {
    return (filterData as FiltersData[FilterType.GROUPS])?.groupIdToData?.[
      subGroupId
    ];
  };

  const getFilteredGroups = (ids: number[]) => {
    return ids.filter((option) => {
      const groupData = getGroupData(option as number);
      if (isAdminWorkspace) return !groupData?.is_personal;
      return groupData?.is_personal;
    });
  };

  const filterValue = useMemo(() => {
    if (isTvToSignalsGroupsFilter) {
      return getFilteredGroups(filterData?.value as number[]);
    }
    return filterData?.value;
  }, [filterData, isAdminWorkspace]);
  const rawFilterOptions = useMemo(() => {
    const options =
      filterData && "options" in filterData ? filterData.options : [];
    if (isTvToSignalsGroupsFilter) {
      return getFilteredGroups(options as number[]);
    }
    return options;
  }, [filterData, isAdminWorkspace]);

  const filterOptions = useMemo(() => {
    let options = rawFilterOptions;
    const shouldSort =
      filterType !== FilterType.AMOUNT_TYPE &&
      (!isMobile || filterType !== FilterType.SYMBOLS);
    if (shouldSort && isStringsArray(options)) {
      options = options.slice().sort((a, b) => {
        const stringSort = sortByStringProperty(a, b);
        if (filterType === FilterType.CLIENTS) {
          const clientA = getAccountByName(a);
          const clientB = getAccountByName(b);
          return (
            +!!clientB?.is_valid - +!!clientA?.is_valid ||
            sortByStringProperty(a, b)
          );
        }
        if (filterType === FilterType.SOURCE_NAME) {
          return (
            +DELETED_ITEM_REGEX.test(a) - +DELETED_ITEM_REGEX.test(b) ||
            sortByStringProperty(a, b)
          );
        }
        return stringSort;
      });
    }
    return options;
  }, [rawFilterOptions, filterType]);

  const getIsSelected: FilterSectionData<T>["getIsSelected"] = (item) => {
    if (
      CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
      Array.isArray(filterValue) &&
      filterValue
    ) {
      return (filterValue as string[]).includes(item as string);
    }
    return false;
  };

  const selectAll = () => {
    dispatch(filtersActions.selectAll([filterType] as any));
  };

  const unselectAll = () => {
    if (
      CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
      Array.isArray(filterValue) &&
      filterValue
    ) {
      onChange([]);
    }
  };

  const displayData: FilterSectionDisplayData<T> = {};
  if (filterType === FilterType.EXCHANGES) {
    (
      displayData as FilterSectionDisplayData<FilterType.EXCHANGES>
    ).getIconProps = (exchange) => {
      return {
        iconType: ICON_TYPES.EXCHANGE,
        coinOrExchangeName: exchange,
      };
    };
    if (config.filterTypes?.includes(FilterType.CLIENTS)) {
      (
        displayData as FilterSectionDisplayData<FilterType.CLIENTS>
      ).getExtraInfo = (exchange: string) => {
        const numExchangeClients =
          mappings.exchangeToClientsMapping?.[exchange]?.length || 0;
        return t(
          "filters.common.clients" as TranslationKeys<LanguageNameSpaces.COMMON>,
          { count: numExchangeClients }
        );
      };
    }
  } else if (filterType === FilterType.CLIENTS) {
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getIconProps =
      (client: string) => {
        return {
          iconType: ICON_TYPES.EXCHANGE,
          coinOrExchangeName: clientToExchangeMapping[client],
        };
      };
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getExtraInfo =
      (client: string) => {
        return clientToExchangeMapping[client];
      };
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getAccountId =
      (client: string) => {
        return getAccountByName(client)?.client_id;
      };
  } else if (filterType === FilterType.SYMBOLS) {
    (displayData as FilterSectionDisplayData<FilterType.SYMBOLS>).getIconProps =
      (symbol: string) => {
        return {
          iconType: ICON_TYPES.COIN,
          coinOrExchangeName: symbol,
        };
      };
  } else if (
    filterType === FilterType.GROUPS ||
    filterType === FilterType.MULTI_GROUPS
  ) {
    (displayData as FilterSectionDisplayData<FilterType.GROUPS>).getIconProps =
      (id: number) => {
        const groupData = getGroupData(id);
        return {
          iconLink: groupData?.image,
          coinOrExchangeName: groupData?.name,
        };
      };
    (displayData as FilterSectionDisplayData<FilterType.GROUPS>).getDisplay = (
      id: number
    ) => {
      const groupData = (filterData as FiltersData[FilterType.GROUPS])
        ?.groupIdToData?.[id];
      return groupData?.name;
    };
  } else if (filterType === FilterType.SIGNAL_STATUS) {
    (
      displayData as FilterSectionDisplayData<FilterType.SIGNAL_STATUS>
    ).getDisplay = (category: EntryZoneCategory) => {
      return t(SIGNAL_STATUS_TO_DISPLAY[category]);
    };
  } else if (filterType === FilterType.FOLLOW_STATUS) {
    (
      displayData as FilterSectionDisplayData<FilterType.FOLLOW_STATUS>
    ).getDisplay = (status) => {
      return t(FOLLOW_STATUS_TO_DISPLAY[status as FollowSignalStatus]);
    };
  } else if (filterType === FilterType.STATUS) {
    (displayData as FilterSectionDisplayData<FilterType.STATUS>).getDisplay = (
      status
    ) => {
      return t(STATUS_TO_DISPLAY[status as Status]);
    };
  } else if (filterType === FilterType.BACKTESTING_STATUS) {
    (
      displayData as FilterSectionDisplayData<FilterType.BACKTESTING_STATUS>
    ).getDisplay = (status) => {
      return t(
        BACKTESTING_STATUS_TO_DISPLAY[status as BacktestingFilterStatus]
      );
    };
  }

  const title = t(FILTER_TYPES_DATA[filterType].title);
  const allSelected = getIsAllSelected(filterType, filterData);

  const getChipsText = () =>
    getChipPreviewText(filterType, filterData, isMobile, t);

  const shouldHide =
    (RANGE_FILTER_TYPES as ReadonlyArray<FilterType>).includes(filterType) &&
    filterType !== FilterType.TIME_FRAME &&
    isNumberArray(filterOptions) &&
    min(filterOptions as number[]) === max(filterOptions as number[]);

  const getResetButtonData = () => {
    const shouldHideResetButton = [
      FilterType.GROUPS,
      FilterType.IS_HOT,
    ].includes(filterType);
    const isResetFilterType = [
      FilterSectionType.PRICE_RANGE,
      FilterSectionType.TIME_RANGE,
      FilterSectionType.SLIDER,
      FilterSectionType.TAGS,
    ].includes(filterSectionType);
    let resetText = t("filters.common.select_all");
    let onClick = selectAll;
    if (isResetFilterType) resetText = t("filters.common.reset");
    else if (allSelected) {
      resetText = t("filters.common.clear_all");
      onClick = unselectAll;
    }

    return {
      shouldHide: shouldHideResetButton,
      resetText,
      onClick,
    };
  };

  const toggleData = useMemo(
    () => ({
      show: showHideDeletedToggle,
      isChecked: hideDeletedToggleActive,
      text: t("filters.toggles.hide_deleted"),
      onChange: () => {
        dispatch(filtersActions.setHideDeletedToggle(!hideDeletedToggleActive));
      },
    }),
    [showHideDeletedToggle, hideDeletedToggleActive]
  );

  return {
    filterSectionType,
    filterType,
    title,
    options: filterOptions,
    value: filterValue ?? [],
    toggleItem,
    onChange,
    displayData,
    getIsSelected,
    selectAll,
    unselectAll,
    allSelected,
    getChipsText,
    shouldHide,
    resetButtonData: getResetButtonData(),
    toggleData,
  };
};
